import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Pagination,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";

import { useApiClient } from "../../api";
import { usePagination } from "../../hooks/usePagination";
import { AcademicYearSelect } from "../AcademicYearSelect";

const SEARCH_DEBOUNCE_MS = 300;

const SchoolClassSelection = ({
  selectedClasses,
  onSelectionChange,
  assignmentTemplate,
  schools = [],
  userRole,
  enabled = true,
  isTeacher = false,
}) => {
  const theme = useTheme();
  const ApiClient = useApiClient();

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const yearGroups = assignmentTemplate?.educationLevel?.yearGroups || [];

  const [selectedSchool, setSelectedSchool] = useState(
    schools.length === 1 ? schools[0] : null,
  );

  const [selectedYearGroup, setSelectedYearGroup] = useState(null);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState();
  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeout);
  }, [searchTerm]);

  useEffect(() => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setSelectedSchool(schools.length === 1 ? schools[0] : null);
    setSelectedYearGroup(null);
  }, [assignmentTemplate?.id]);

  const filters = useMemo(
    () => ({
      userRole,
      searchTerm: debouncedSearchTerm,
      school: selectedSchool?.id || null,
      educationLevel: assignmentTemplate?.educationLevel?.id || null,
      yearGroup: selectedYearGroup?.id || null,
      academicYear: selectedAcademicYear || null,

      subjects:
        assignmentTemplate?.subjects?.map((subject) => subject.id) || [],
    }),
    [
      userRole,
      debouncedSearchTerm,
      selectedSchool,
      assignmentTemplate,
      selectedYearGroup,
      selectedAcademicYear,
    ],
  );

  const {
    options: schoolClasses,
    loading,
    error,
    page,
    pageInfo,
    handlePageChange,
  } = usePagination(
    ({ first, after }) =>
      ApiClient.schools.getSchoolClasses({
        first,
        after,
        ...filters,
      }),
    20,
    filters,
    enabled && Boolean(assignmentTemplate?.id),
  );

  const selectedClassIds = useMemo(
    () => new Set(selectedClasses.map((schoolClass) => schoolClass.id)),
    [selectedClasses],
  );

  const availableClasses = useMemo(
    () =>
      schoolClasses.filter(
        (schoolClass) => !selectedClassIds.has(schoolClass.id),
      ),
    [schoolClasses, selectedClassIds],
  );

  const handleSelect = (schoolClass) => {
    if (selectedClassIds.has(schoolClass.id)) {
      return;
    }

    onSelectionChange([...selectedClasses, schoolClass]);
  };

  const handleRemove = (schoolClass) => {
    onSelectionChange(
      selectedClasses.filter(
        (selectedClass) => selectedClass.id !== schoolClass.id,
      ),
    );
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h5">Selected Classes</Typography>

        {selectedClasses.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            No classes selected.
          </Typography>
        ) : (
          <Box
            sx={{
              maxHeight: 250,
              overflowY: "auto",
            }}
          >
            {selectedClasses.map((schoolClass) => (
              <Card
                key={schoolClass.id}
                sx={{
                  mt: 1,
                  backgroundColor: alpha(theme.palette.primary.main, 0.2),
                }}
              >
                <CardContent>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={2}
                  >
                    <Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle1" fontWeight="bold">
                          {schoolClass.name}
                        </Typography>

                        {schoolClass.userIsTeacher && (
                          <Chip label="My Class" size="small" />
                        )}
                      </Stack>

                      {schoolClass.school?.name && (
                        <Typography variant="body2" color="text.secondary">
                          {schoolClass.school.name}
                        </Typography>
                      )}

                      {schoolClass.academicYear?.name && (
                        <Typography variant="body2" color="text.secondary">
                          {schoolClass.academicYear.name}
                        </Typography>
                      )}
                    </Stack>

                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => handleRemove(schoolClass)}
                    >
                      Remove
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Box>

      <Box>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Available Classes
        </Typography>

        <Stack
          direction={{
            xs: "column",
            md: "row",
          }}
          spacing={2}
          sx={{
            mb: 2,
            "& .MuiInputBase-root": {
              height: 40,
            },
          }}
        >
          <TextField
            label="Search"
            placeholder="Search school or class name"
            size="small"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            sx={{ flex: 1 }}
          />

          <Autocomplete
            options={schools}
            value={selectedSchool}
            onChange={(_event, school) => {
              if (schools.length > 1) {
                setSelectedSchool(school);
              }
            }}
            disabled={schools.length === 1}
            disableClearable={schools.length === 1}
            getOptionLabel={(school) => school?.name || ""}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="School"
                placeholder={schools.length === 1 ? undefined : "All schools"}
                size="small"
              />
            )}
            sx={{
              width: {
                xs: "100%",
                md: 250,
              },
            }}
          />

          <AcademicYearSelect
            value={selectedAcademicYear}
            onChange={setSelectedAcademicYear}
            size="small"
            sx={{ width: { xs: "100%", md: 210 } }}
          />

          <Autocomplete
            options={yearGroups}
            value={selectedYearGroup}
            onChange={(_event, yearGroup) => setSelectedYearGroup(yearGroup)}
            getOptionLabel={(yearGroup) =>
              yearGroup?.yearGroup
                ? `Year ${yearGroup.yearGroup}`
                : yearGroup?.name || ""
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Year Group"
                placeholder="All year groups"
                size="small"
              />
            )}
            sx={{
              width: {
                xs: "100%",
                md: 200,
              },
            }}
          />
        </Stack>

        <Box
          sx={{
            position: "relative",
            minHeight: 100,
          }}
        >
          {loading && schoolClasses.length > 0 && (
            <LinearProgress sx={{ mb: 2 }} />
          )}

          {error ? (
            <Typography color="error" align="center" sx={{ py: 4 }}>
              Failed to load available classes.
            </Typography>
          ) : !loading && schoolClasses.length === 0 ? (
            <Card sx={{ mt: 1 }}>
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  minHeight: 150,
                }}
              >
                <Typography variant="h5">No available classes</Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  {searchTerm || selectedSchool
                    ? "No classes match the current filters."
                    : isTeacher
                      ? "Ask an administrator to create new classes."
                      : "No classes are currently available."}
                </Typography>
              </CardContent>
            </Card>
          ) : availableClasses.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ py: 4 }}
            >
              All classes on this page are selected.
            </Typography>
          ) : (
            <Box
              sx={{
                maxHeight: 400,
                overflowY: "auto",
              }}
            >
              {availableClasses.map((schoolClass) => (
                <Card key={schoolClass.id}>
                  <CardContent>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={2}
                    >
                      <Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="subtitle1" fontWeight="bold">
                            {schoolClass.name}
                          </Typography>

                          {schoolClass.userIsTeacher && (
                            <Chip label="My Class" size="small" />
                          )}
                        </Stack>

                        {schoolClass.school?.name && (
                          <Typography variant="body2" color="text.secondary">
                            {schoolClass.school.name}
                          </Typography>
                        )}

                        {schoolClass.academicYear?.name && (
                          <Typography variant="body2" color="text.secondary">
                            {schoolClass.academicYear.name}
                          </Typography>
                        )}
                      </Stack>

                      <Button
                        variant="contained"
                        onClick={() => handleSelect(schoolClass)}
                      >
                        Select
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}

          {(page > 1 || pageInfo?.hasNextPage) && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                page={page}
                onChange={handlePageChange}
                count={pageInfo?.hasNextPage ? page + 1 : page}
                color="primary"
                disabled={loading}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Stack>
  );
};

export { SchoolClassSelection };
